/**
 * MASTER_PLAN.md §3.9 work item #14 — client-side geocoding for CSV
 * import. LocationService.findOrCreate on the backend hard-requires
 * lat/lng and there is no backend geocoding integration anywhere in
 * this codebase, so rows are geocoded here, before the preview/commit
 * round trip. Doing it client-side also keeps the 1,084-row import off
 * Lambda entirely for the slow part (MASTER_PLAN §3.7 "handles 1,084
 * rows without timeout" is about the API, not the browser).
 *
 * Google's Geocoder is callback-based and rate-limited server-side
 * (OVER_QUERY_LIMIT). This runs a small concurrency window rather than
 * firing all requests at once, then does one retry pass for anything
 * that got rate-limited.
 */

export interface GeocodeInput {
  rowIndex: number;
  line1: string;
  city: string;
  state: string;
  zip?: string;
}

export interface GeocodeResult {
  rowIndex: number;
  latitude: number | null;
  longitude: number | null;
  error?: string;
}

function geocodeOne(
  geocoder: google.maps.Geocoder,
  input: GeocodeInput,
): Promise<GeocodeResult> {
  const address = [input.line1, input.city, input.state, input.zip].filter(Boolean).join(", ");
  return new Promise((resolve) => {
    geocoder.geocode({ address }, (results, status) => {
      if (status === google.maps.GeocoderStatus.OK && results && results[0]) {
        const loc = results[0].geometry.location;
        resolve({ rowIndex: input.rowIndex, latitude: loc.lat(), longitude: loc.lng() });
      } else {
        resolve({
          rowIndex: input.rowIndex,
          latitude: null,
          longitude: null,
          error: String(status),
        });
      }
    });
  });
}

export async function geocodeRows(
  inputs: GeocodeInput[],
  onProgress?: (done: number, total: number) => void,
): Promise<GeocodeResult[]> {
  if (!inputs.length) return [];

  if (!(window as any).google?.maps) {
    return inputs.map((i) => ({
      rowIndex: i.rowIndex,
      latitude: null,
      longitude: null,
      error: "GOOGLE_MAPS_NOT_LOADED",
    }));
  }

  const geocoder = new google.maps.Geocoder();
  const results: GeocodeResult[] = new Array(inputs.length);
  const CONCURRENCY = 5;
  let cursor = 0;
  let done = 0;

  async function worker() {
    while (cursor < inputs.length) {
      const idx = cursor++;
      results[idx] = await geocodeOne(geocoder, inputs[idx]);
      done++;
      onProgress?.(done, inputs.length);
    }
  }

  await Promise.all(Array.from({ length: CONCURRENCY }, worker));

  // Retry pass for rate-limited rows, with a short backoff before
  // retrying — sequential this time, not another concurrency burst.
  const retryIndices = results.reduce<number[]>((acc, r, idx) => {
    if (r?.error === "OVER_QUERY_LIMIT") acc.push(idx);
    return acc;
  }, []);

  if (retryIndices.length) {
    await new Promise((r) => setTimeout(r, 1500));
    for (const idx of retryIndices) {
      results[idx] = await geocodeOne(geocoder, inputs[idx]);
    }
  }

  return results;
}
